import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';
import {
    computeBarPosition,
    isOverdue,
    getCompletionPct,
    formatMs,
    getFullName,
    getPhotoUrl,
    getInitials
} from './timelineUtils';
import { ROW_HEIGHT } from './constants';

function MiniAvatar({ assignee }) {
    const [imgErr, setImgErr] = useState(false);

    if (!assignee) {
        return (
            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-400 ring-2 ring-white flex-shrink-0 overflow-hidden">
                ?
            </div>
        );
    }
    const name = getFullName(assignee);
    const photoUrl = getPhotoUrl(assignee?.photo);
    // When photo fails to load, imgErr flips to true → gradient + initials shown
    const show = photoUrl && !imgErr;
    return (
        <div
            className="w-7 h-7 rounded-full overflow-hidden ring-2 ring-white flex items-center justify-center text-xs font-bold text-white flex-shrink-0 shadow-sm"
            style={show ? {} : { background: 'linear-gradient(90deg,#03488A 0%,#00AAF1 100%)' }}
        >
            {show
                ? <img src={photoUrl} alt={name} className="w-full h-full object-cover rounded-full" onError={() => setImgErr(true)} />
                : getInitials(name)
            }
        </div>
    );
}

export default function TimelineGanttBar({
    task,
    section,
    gridStart,
    gridEnd,
    gridWidthPx,
    onTaskClick,
}) {
    const overdue = isOverdue(task);
    const isCompleted = task.status === 'Completed';
    const isOnHold = task.status === 'On Hold';
    const pct = getCompletionPct(task);
    const position = computeBarPosition(task, gridStart, gridEnd);

    // ── No valid date range — render nothing ───────────────────────────────────
    if (!position) return null;

    // ── Colors — barTrack = tinted shell, barBg = solid darker progress fill ──
    const progressBg = section.barBg;
    const trackBg = section.barTrack;
    const trackBorder = section.borderColor;

    // Track is a tinted color (not white) — dark text reads fine on it.
    // Progress fill is the solid darker shade — white text reads on it.
    const titleClass = pct > 50 ? 'text-white' : 'text-[#191c1d]';
    const subClass = pct > 50 ? 'text-white/80' : 'text-[#424750]';
    const pctClass = 'text-xs font-bold ' + (pct > 50 ? 'text-white' : 'text-[#191c1d]');

    const startLabel = task.startDate ? dayjs(task.startDate).format('D MMM') : null;
    const endLabel = task.dueDate ? dayjs(task.dueDate).format('D MMM') : null;

    const tooltipContent = (
        <div className="text-xs space-y-1 max-w-[220px]">
            <p className="font-bold text-white leading-tight">{task.title}</p>
            {task.project?.name && <p className="text-slate-300">{task.project.name}</p>}
            {(startLabel || endLabel) && (
                <p className="text-slate-300">
                    {startLabel ? `${startLabel} → ` : ''}{endLabel || '—'}
                </p>
            )}
            {task.estimatedTime && (
                <p className="text-slate-300">
                    Est: {task.estimatedTime}
                    {task.actualTimeMs > 0 ? ` / Actual: ${formatMs(task.actualTimeMs)}` : ''}
                </p>
            )}
            <p className="text-slate-300">Progress: {pct}%</p>
            {overdue && <p className="text-red-400 font-bold">⚠ Overdue</p>}
        </div>
    );

    return (
        <div className="relative w-full flex items-center" style={{ height: ROW_HEIGHT }}>
            <Tooltip
                title={tooltipContent}
                placement="top"
                color="#1e293b"
                overlayInnerStyle={{ padding: '8px 12px', borderRadius: 8 }}
            >
                <div
                    className={`absolute h-10 rounded-lg flex items-center px-3 shadow-sm cursor-pointer transition-transform hover:-translate-y-0.5 overflow-hidden ${trackBg} border ${trackBorder}`}
                    style={{
                        left: `${position.leftFraction * 100}%`,
                        width: `${position.widthFraction * 100}%`,
                        minWidth: '160px',
                    }}
                    onClick={() => onTaskClick?.(task)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && onTaskClick?.(task)}
                    aria-label={`${task.title} — click to open details`}
                >
                    {/* Progress fill */}
                    {pct > 0 && (
                        <div
                            className={`absolute left-0 top-0 bottom-0 rounded-l-lg transition-all duration-500 ${progressBg}`}
                            style={{ width: `${pct}%` }}
                        />
                    )}

                    {/* Content layer */}
                    <div className="relative z-10 flex items-center justify-between w-full">
                        <div className="flex items-center gap-2 min-w-0">
                            <MiniAvatar assignee={task.assignee} />
                            <div className="flex flex-col min-w-0">
                                <span className={`text-xs font-bold leading-none truncate max-w-[130px] flex items-center gap-1 ${titleClass}`}>
                                    {task.itemType === 'milestone' && (
                                        <span className="text-amber-400 font-extrabold text-sm shrink-0">◆</span>
                                    )}
                                    <span className="truncate">{task.title}</span>
                                </span>
                                {task.project?.name && (
                                    <span className={`text-xs truncate max-w-[130px] mt-0.5 ${subClass}`}>
                                        {task.project.name}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0 ml-6">
                            {pct > 0 && (
                                <span className={pctClass}>{pct}%</span>
                            )}

                            {overdue && (
                                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5 whitespace-nowrap font-bold">
                                    <span className="material-symbols-outlined text-xs">warning</span> Delayed
                                </span>
                            )}

                            {isOnHold && !overdue && (
                                <span className="bg-orange-400/20 text-orange-700 text-xs px-1.5 py-0.5 rounded font-bold whitespace-nowrap">
                                    ⏸ On Hold
                                </span>
                            )}

                            {isCompleted && (
                                <span className="bg-emerald-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5 whitespace-nowrap font-bold">
                                    <span className="material-symbols-outlined text-xs">check_circle</span> Done
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </Tooltip>
        </div>
    );
}
